import { Dtos, type UserId } from "@inrev/common";
import { DateTime } from "luxon";
import { type EntityScope, scopeMatches } from "../../../shared/scope";

type AccountPreview = Pick<
	Dtos.SuretyAccount.Preview.Get.Response,
	"status" | "hasBeenActive" | "activatedAt" | "underwriterUserId" | "submittedAt" | "createdAt"
>;

export type SubmissionMetricsInput = {
	accountPreviews: AccountPreview[];
	scope: EntityScope;
	currentUserId: UserId;
	/** Injectable "now" for deterministic trend windows in tests. */
	nowMs?: number;
};

export type SubmissionTrend = { current: number; prior: number };

export type SubmissionMetrics = {
	open: number;
	inReview: number;
	approved: number;
	ttmSubmissions: SubmissionTrend;
	ttmActivated: SubmissionTrend;
	/** Monthly inflow sparklines (12 buckets, oldest → newest) for each card. */
	series: {
		open: number[];
		inReview: number[];
		approved: number[];
		ttmSubmissions: number[];
		ttmActivated: number[];
	};
};

const TREND_SERIES_BUCKETS = 12;

const toMs = (value: string): number => DateTime.fromISO(value).toMillis();

// Buckets event timestamps into the trailing 12 calendar months, oldest → newest.
const buildMonthlySeries = (timestampsMs: number[], nowMs: number): number[] => {
	const series = new Array<number>(TREND_SERIES_BUCKETS).fill(0);
	const now = DateTime.fromMillis(nowMs);
	for (const atMs of timestampsMs) {
		const monthsAgo = Math.floor(now.diff(DateTime.fromMillis(atMs), "months").months);
		if (monthsAgo < 0 || monthsAgo >= TREND_SERIES_BUCKETS) continue;
		series[TREND_SERIES_BUCKETS - 1 - monthsAgo] += 1;
	}
	return series;
};

export const computeSubmissionMetrics = ({
	accountPreviews,
	scope,
	currentUserId,
	nowMs,
}: SubmissionMetricsInput): SubmissionMetrics => {
	const now = nowMs ?? DateTime.now().toMillis();
	const windowStartMs = DateTime.fromMillis(now).minus({ months: 12 }).toMillis();
	const priorStartMs = DateTime.fromMillis(now).minus({ months: 24 }).toMillis();

	const scoped = accountPreviews.filter((account) =>
		scopeMatches(scope, account.underwriterUserId, currentUserId),
	);

	let inReview = 0;
	let approved = 0;
	let ttmSubmissionsCurrent = 0;
	let ttmSubmissionsPrior = 0;
	let ttmActivatedCurrent = 0;
	let ttmActivatedPrior = 0;
	// Open/In Review/Approved series show the submission-age distribution of the
	// currently-open accounts they count; only TTM tracks inflow across all accounts.
	const inReviewSubmittedAtMs: number[] = [];
	const approvedSubmittedAtMs: number[] = [];
	const ttmSubmittedAtMs: number[] = [];
	const ttmActivatedAtMs: number[] = [];

	for (const account of scoped) {
		const submittedMs = toMs(account.submittedAt ?? account.createdAt);

		if (!account.hasBeenActive) {
			if (account.status === "review") {
				inReview += 1;
				inReviewSubmittedAtMs.push(submittedMs);
			} else if (account.status === "approved") {
				approved += 1;
				approvedSubmittedAtMs.push(submittedMs);
			}
		}

		ttmSubmittedAtMs.push(submittedMs);
		// Exclusive lower bound so a submission exactly 12 months old falls outside
		// the current window, matching buildMonthlySeries (which drops monthsAgo >= 12).
		if (submittedMs > windowStartMs && submittedMs <= now) ttmSubmissionsCurrent += 1;
		else if (submittedMs >= priorStartMs && submittedMs <= windowStartMs) ttmSubmissionsPrior += 1;

		if (!account.activatedAt) continue;
		const activatedMs = toMs(account.activatedAt);
		ttmActivatedAtMs.push(activatedMs);
		if (activatedMs > windowStartMs && activatedMs <= now) ttmActivatedCurrent += 1;
		else if (activatedMs >= priorStartMs && activatedMs <= windowStartMs) ttmActivatedPrior += 1;
	}

	const inReviewSeries = buildMonthlySeries(inReviewSubmittedAtMs, now);
	const approvedSeries = buildMonthlySeries(approvedSubmittedAtMs, now);

	return {
		open: inReview + approved,
		inReview,
		approved,
		ttmSubmissions: { current: ttmSubmissionsCurrent, prior: ttmSubmissionsPrior },
		ttmActivated: { current: ttmActivatedCurrent, prior: ttmActivatedPrior },
		series: {
			open: inReviewSeries.map((v, i) => v + approvedSeries[i]),
			inReview: inReviewSeries,
			approved: approvedSeries,
			ttmSubmissions: buildMonthlySeries(ttmSubmittedAtMs, now),
			ttmActivated: buildMonthlySeries(ttmActivatedAtMs, now),
		},
	};
};
